import type {
  DataLayerEventName,
  DataLayerObject,
  DataLayerPageStatus,
  EventData,
  OptimizelyDecisionTracking,
  PageViewDataLayerObject,
  PushableObject,
} from '@/app_lib/types/Gtm';

export interface TrackEventParams {
  data: EventData;
  eventType?: DataLayerEventName;
}

export const pushToDataLayer = (obj: PushableObject) => {
  window.dataLayer?.push(obj);
};

export const trackEvent = (data: EventData, eventType: DataLayerEventName = 'eventTracker') => {
  const trackingData: DataLayerObject = {
    event: eventType,
    ...data,
    nonInteraction: 0,
  };

  Object.keys(data).forEach((key) => {
    if (
      [
        'eventCategory',
        'contentPlacementCD',
        'CompanyCD',
        'rankingTitleCD',
        'userIDCD',
        'eventAction',
        'eventLabel',
      ].includes(key) &&
      data[key]
    ) {
      trackingData[key] = data[key].toLowerCase();
    }
  });

  const pageViewData = window.dataLayer?.find(
    (item) => (item as PageViewDataLayerObject).event === 'fortune.historyChange',
  );
  const optimizelyData =
    window.dataLayer?.filter((item) => (item as OptimizelyDecisionTracking).event === 'optimizely-decision') || [];
  const optimizelyVariationNames = optimizelyData
    .map((item) => (item as OptimizelyDecisionTracking).variationKey)
    .filter((name) => name !== undefined)
    .join(',');

  const cmsCategoryCd = (pageViewData as PageViewDataLayerObject)?.cmsCategoryCD?.toLowerCase() || '';

  pushToDataLayer({
    ...trackingData,
    // biome-ignore lint/style/useNamingConvention: external data
    cmsCategoryCD: cmsCategoryCd,
    optimizely: [...optimizelyData],
    optimizelyVariationNames,
  });
};

export const GtmPageView = (data: PageViewDataLayerObject) => {
  const trackingData: PageViewDataLayerObject = {
    event: 'fortune.historyChange',
    ...data,
  };
  const optimizelyData =
    window.dataLayer?.filter((item) => (item as OptimizelyDecisionTracking).event === 'optimizely-decision') || [];
  const optimizelyVariationNames = optimizelyData
    .map((item) => (item as OptimizelyDecisionTracking).variationKey)
    .filter((name) => name !== undefined)
    .join(',');

  pushToDataLayer({ ...trackingData, optimizely: [...optimizelyData], optimizelyVariationNames });
};

export const getGtmPageStatusEvent = (
  isError: boolean,
  errorCode?: number,
  errorDetail?: string,
): DataLayerPageStatus => ({
  errorCode: errorCode ?? 0,
  errorDetail: errorDetail ?? '',
  event: `Page Status - ${isError ? `Error (${errorCode})` : 'Init'}`,
  isError,
});
